import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, ArrowLeft, Bell, Building2, Check, ChevronDown, CircleDollarSign, Cpu, Eye, Globe2, ShieldAlert, Star, Upload, X } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "../lib/toast";
import { apiRequest } from "../../services/apiClient";
import { useSimplAI } from "../../imports/useSimpleAI";

type Tab = "overview" | "scorecard" | "geo" | "watchlist";
type Phase = "select" | "running" | "complete";
type AddMode = "single" | "bulk" | null;
type AssessmentSupplier = { id?: number | string; supplier: string; region: string | null; industry: string | null; assessmentSource?: "runtime" | "database" };
type AgentRow = Record<string, any>;
type ScoreEntry = { label: string; score: number; note: string };

type Article = {
  title?: string;
  publisher?: string;
  url?: string;
  summary?: string;
};

type SubParameterDetail = {
  name: string;
  score: number;
  confidenceScore?: string;
  rationale?: string;
  assessmentBasis?: string;
  articles?: Article[];
  sources?: string[];
};

type ParameterDetail = {
  name: string;
  score: number;
  confidenceScore?: string;
  riskClassification?: string;
  summary?: string;
  subParameters?: SubParameterDetail[];
};

type RiskSupplier = {
  name: string;
  assessmentQuarter?: string;
  region: string;
  category: string;
  overall: number;
  overallRiskClassification?: string;
  parameters: string[];
  parameterScores: ScoreEntry[];
  subParameters: ScoreEntry[];
  news: string;
  links: string[];
  parameterDetails?: ParameterDetail[];
};

type RiskTrendPoint = {
  quarter: string;
  supplier: string;
  overall: number;
  aiSummary: string;
  parameters: Record<string, number>;
};

type RiskTrendData = {
  quarters: string[];
  points: RiskTrendPoint[];
};

const SPEND_AI_RISK_TOOL_ID = 54;

const steps = [
  "Sending selected suppliers to the risk agent",
  "Scanning news, filings and public disclosures",
  "Scoring macroeconomic and geopolitical exposure",
  "Scoring supply chain and operational parameters",
  "Scoring litigation and corruption parameters",
  "Computing parameter and composite risk scores",
  "Building supplier scorecard and watchlist",
];

const RISK_BANDS = [
  { label: "Low Risk", className: "low", zone: "Monitor" },
  { label: "Medium Risk", className: "medium", zone: "Manage" },
  { label: "High Risk", className: "high", zone: "Critical" },
] as const;

const scoreBand = (score: number, classification?: string) => {
  const norm = (classification || "").toLowerCase();
  if (norm.includes("very low") || norm.includes("very-low") || norm === "low risk" || norm === "low") return "low";
  if (norm.includes("medium") || norm.includes("moderate")) return "medium";
  if (norm.includes("very high") || norm.includes("very-high") || norm.includes("critical") || norm === "high risk" || norm === "high") return "high";

  if (score >= 7) return "low";
  if (score >= 5) return "medium";
  return "high";
};

const classificationBand = (classification?: string) => {
  const norm = (classification || "").toLowerCase().trim();
  if (norm.includes("very low") || norm.includes("very-low") || norm === "low risk" || norm === "low") return "low";
  if (norm.includes("medium") || norm.includes("moderate")) return "medium";
  if (norm.includes("very high") || norm.includes("very-high") || norm.includes("critical") || norm === "high risk" || norm === "high") return "high";
  return "";
};

const normalizeKey = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

const getField = (row: AgentRow, ...keys: string[]) => {
  const lookup = new Map(Object.keys(row || {}).map(key => [normalizeKey(key), row[key]]));
  for (const key of keys) {
    const value = lookup.get(normalizeKey(key));
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return "";
};

const toNumber = (value: unknown) => {
  const match = String(value ?? "").match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : 0;
};

const splitList = (value: unknown) => String(value ?? "").split(/[,;\n]/).map(item => item.trim()).filter(Boolean);

const canonicalParameterKey = (value: string) => normalizeKey(value)
  .replace(/macroeconomics/g, "macroeconomic")
  .replace(/factors/g, "factor")
  .replace(/risks/g, "risk")
  .replace(/certifications/g, "certification");

// Matches "Label: 7 (note)" pairs so commas inside the note text do not break parsing.
const parseScoreEntries = (value: unknown): ScoreEntry[] => {
  const text = String(value ?? "");
  if (text.startsWith("{") && text.endsWith("}")) {
    try {
      const object = JSON.parse(text) as Record<string, unknown>;
      return Object.entries(object).map(([label, score]) => ({ label, score: toNumber(score), note: "" }));
    } catch {
      // Continue with the text parser for non-JSON agent output.
    }
  }
  const pattern = /([^:;,\n()]+?)\s*:\s*(\d+(?:\.\d+)?)\s*(?:\(([^)]*)\))?/g;
  const entries: ScoreEntry[] = [];
  let match = pattern.exec(text);

  while (match) {
    const label = match[1].replace(/^[\s,;-]+/, "").trim();
    if (label) entries.push({ label, score: Number(match[2]), note: (match[3] || "").trim() });
    match = pattern.exec(text);
  }

  return entries;
};

const STOP_WORDS = new Set(["and", "the", "of", "or", "risk", "risks", "factor", "factors", "score", "scores", "index"]);

const tokenize = (value: string) => value.toLowerCase().split(/[^a-z0-9]+/).filter(token => token.length > 2 && !STOP_WORDS.has(token));

const similarity = (left: string, right: string) => {
  const leftTokens = tokenize(left);
  const rightTokens = tokenize(right);

  return leftTokens.reduce((total, token) => {
    const hit = rightTokens.some(other => token === other || (token.length >= 4 && other.length >= 4 && (token.startsWith(other.slice(0, 4)) || other.startsWith(token.slice(0, 4)))));
    return hit ? total + 1 : total;
  }, 0);
};

const findParameterScore = (parameter: string, entries: ScoreEntry[]) => {
  const exact = entries.find(entry => canonicalParameterKey(entry.label) === canonicalParameterKey(parameter));
  if (exact) return exact;

  let best: ScoreEntry | null = null;
  let bestScore = 0;

  for (const entry of entries) {
    const score = similarity(parameter, entry.label);
    if (score > bestScore) {
      best = entry;
      bestScore = score;
    }
  }

  return bestScore > 0 ? best : null;
};

const hasLowParameterRisk = (supplier: RiskSupplier) =>
  supplier.parameterScores.some(entry => entry.score < 6.5);

const healthLabel = (score: number) => score >= 7 ? "Strong" : score >= 6.5 ? "Moderate" : "Weak";

const ScoreWithBar = ({ score }: { score: number }) => (
  <span className="tprm-score-with-bar">
    <strong>{score}/10</strong>
    <i><b style={{ width: `${Math.min(100, Math.max(0, score * 10))}%` }} /></i>
  </span>
);

const emptyRiskTrendData: RiskTrendData = { quarters: [], points: [] };

const quarterSortKey = (quarter: string) => {
  const match = quarter.match(/Q([1-4])\s+(\d{4})/i);
  return match ? Number(match[2]) * 10 + Number(match[1]) : Number.MAX_SAFE_INTEGER;
};

const getDisplayQuarter = (mapping: any) => {
  const namedQuarter = String(mapping?.quarter_name || "").match(/Q([1-4])\s+(\d{4})/i);
  if (namedQuarter) return `Q${namedQuarter[1]} ${namedQuarter[2]}`;

  const quarter = String(mapping?.quarter_number || mapping?.source_node || "").match(/Q?([1-4])/i);
  const year = String(mapping?.quarter_year || "").match(/\d{4}/)?.[0];
  return quarter && year ? `Q${quarter[1]} ${year}` : "";
};

const buildRiskTrendData = (value: any): RiskTrendData => {
  const output = value?.data?.output || value?.output || {};
  const rawMappings = Array.isArray(output.quarter_mapping) ? output.quarter_mapping : [];
  const mappings = rawMappings.map((mapping: any) => ({
    ...mapping,
    displayQuarter: getDisplayQuarter(mapping),
  })).filter((mapping: any) => mapping.displayQuarter);
  const warnings = Array.isArray(output.processing_warnings) ? output.processing_warnings : [];
  const quarters = mappings.map((mapping: any) => mapping.displayQuarter).filter(Boolean);
  const points: RiskTrendPoint[] = [];

  warnings.forEach((warning: any) => {
    const quarter = String(mappings.find((mapping: any) => mapping.source_node === warning.source_node)?.displayQuarter || "");
    if (!quarter || !Array.isArray(warning.outputs)) return;
    warning.outputs.forEach((item: any) => {
      const answer = item?.answer;
      if (!answer || !answer.company_name) return;
      const parameters: Record<string, number> = {};
      (Array.isArray(answer.parameters) ? answer.parameters : []).forEach((parameter: any) => {
        const name = String(parameter.parameter_name || parameter.name || "").trim();
        if (name) parameters[name] = toNumber(parameter.parameter_score ?? parameter.score);
      });
      points.push({
        quarter,
        supplier: String(answer.company_name).trim(),
        overall: toNumber(answer.overall_score),
        aiSummary: String(answer.ai_summary || "").trim(),
        parameters,
      });
    });
  });

  const orderedQuarters: string[] = Array.from(new Set<string>(quarters)).sort((left, right) => quarterSortKey(left) - quarterSortKey(right));
  return { quarters: orderedQuarters, points };
};

const buildQuarterlyRiskSuppliers = (value: any, selected: AssessmentSupplier[]) => {
  const output = value?.data?.output || value?.output || {};
  const rawMappings = Array.isArray(output.quarter_mapping) ? output.quarter_mapping : [];
  const mappings = rawMappings.map((mapping: any) => ({
    ...mapping,
    displayQuarter: getDisplayQuarter(mapping),
  })).filter((mapping: any) => mapping.displayQuarter);
  const byQuarter: Record<string, RiskSupplier[]> = {};
  (Array.isArray(output.processing_warnings) ? output.processing_warnings : []).forEach((warning: any) => {
    const mapping = mappings.find((item: any) => item.source_node === warning.source_node);
    const quarter = String(mapping?.displayQuarter || "").trim();
    if (!quarter || !Array.isArray(warning.outputs)) return;
    const rows = collectAgentRows(warning.outputs);
    const uniqueRows = Array.from(new Map(rows.map(row => {
      const companyName = String(getField(row, "company_name", "companyName", "Supplier_Name", "Supplier")).trim();
      return [normalizeKey(companyName), row] as const;
    })).values());
    const quarterSuppliers = [
      ...(byQuarter[quarter] || []),
      ...uniqueRows.map(row => toRiskSupplier(row, selected, quarter)),
    ];
    byQuarter[quarter] = Array.from(new Map(
      quarterSuppliers.map(supplier => [normalizeKey(supplier.name), supplier] as const)
    ).values());
  });
  return byQuarter;
};

const trendColor = ["#05b887", "#3265aa", "#7540ef", "#f26a18", "#df0b57", "#0b8fa8", "#9b59b6", "#8a6d3b", "#44546a", "#e0a11a"];

function SupplierRiskTrendChart({ trendData }: { trendData: RiskTrendData }) {
  const supplierPickerRef = useRef<HTMLDetailsElement>(null);
  const trendChartRef = useRef<HTMLDivElement>(null);
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  const suppliers = Array.from(new Set(trendData.points.map(point => point.supplier)));
  const rankedSuppliers = suppliers.sort((left, right) => {
    const latest = (supplier: string) => [...trendData.points].reverse().find(point => point.supplier === supplier)?.overall ?? 0;
    return latest(right) - latest(left);
  });
  const defaultSuppliers = rankedSuppliers.slice(0, Math.min(10, rankedSuppliers.length));
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>(defaultSuppliers);
  const [parameter, setParameter] = useState("overall");
  const [hoveredPoint, setHoveredPoint] = useState<{ supplier: string; quarter: string; score: number; summary: string; color: string; x: number; y: number } | null>(null);
  const parameters = Array.from(new Set(trendData.points.flatMap(point => Object.keys(point.parameters))));
  const chartSeries = selectedSuppliers.map((supplier, index) => ({ supplier, key: `supplier-${index}`, color: trendColor[index % trendColor.length] }));
  useEffect(() => {
    const closePicker = (event: MouseEvent) => {
      if (supplierPickerRef.current && !supplierPickerRef.current.contains(event.target as Node)) setSupplierPickerOpen(false);
    };
    document.addEventListener("mousedown", closePicker);
    return () => document.removeEventListener("mousedown", closePicker);
  }, []);
  const chartData = trendData.quarters.map(quarter => {
    const row: Record<string, string | number> = { quarter };
    chartSeries.forEach(({ supplier, key }) => {
      const point = trendData.points.find(item => item.quarter === quarter && item.supplier === supplier);
      row[key] = point ? parameter === "overall" ? point.overall : point.parameters[parameter] ?? 0 : 0;
      row[`summary-${key}`] = point?.aiSummary || "";
    });
    return row;
  });
  const scores = trendData.points.map(point => parameter === "overall" ? point.overall : point.parameters[parameter] ?? 0);
  const yMin = Math.max(0, Math.floor((Math.min(...scores) - 0.3) * 10) / 10);
  const yMax = Math.min(10, Math.ceil((Math.max(...scores) + 0.3) * 10) / 10);
  const yTicks = Array.from({ length: Math.floor((yMax - yMin) / 0.5) + 1 }, (_, index) => Number((yMin + index * 0.5).toFixed(1)));

  if (!trendData.points.length) return null;

  const toggleSupplier = (supplier: string) => {
    setSelectedSuppliers(current => current.includes(supplier)
      ? current.filter(item => item !== supplier)
      : current.length < 10 ? [...current, supplier] : current);
  };

  const getTooltipPlacement = (point: NonNullable<typeof hoveredPoint>) => {
    const chartBox = trendChartRef.current;
    const chartWidth = chartBox?.clientWidth || 0;
    const chartHeight = chartBox?.clientHeight || 390;
    const tooltipWidth = 300;
    const gap = 12;
    // Left is a plain pixel value clamped to the chart's own bounds, so the
    // tooltip can never spill into the sidebar/header regardless of where
    // the chart sits on the page.
    const left = Math.min(Math.max(point.x - tooltipWidth / 2, 0), Math.max(chartWidth - tooltipWidth, 0));
    const isTopHalf = point.y < chartHeight / 2;
    const top = isTopHalf ? point.y + gap : point.y - gap;
    return { left, top, transform: isTopHalf ? "translateY(0)" : "translateY(-100%)" };
  };

  return <section className="tprm-panel tprm-risk-trend-panel">
    <div className="tprm-chart-head"><div><h3>Supplier Risk Trend</h3><p>Quarterly risk scores by supplier</p></div><div className="tprm-trend-controls">
      <label><span className="tprm-sr-only">Risk parameter</span><select value={parameter} onChange={event => setParameter(event.target.value)}><option value="overall">Overall</option>{parameters.map(name => <option value={name} key={name}>{name}</option>)}</select></label>
      <details ref={supplierPickerRef} className="tprm-supplier-picker" open={supplierPickerOpen}><summary onClick={event => { event.preventDefault(); setSupplierPickerOpen(open => !open); }}>{selectedSuppliers.length} Suppliers</summary><div>{rankedSuppliers.map(supplier => <label key={supplier}><input type="checkbox" checked={selectedSuppliers.includes(supplier)} onChange={() => toggleSupplier(supplier)} disabled={!selectedSuppliers.includes(supplier) && selectedSuppliers.length >= 10} />{supplier}</label>)}<small>Max 10 suppliers</small></div></details>
    </div></div>
    <div ref={trendChartRef} className="tprm-risk-trend-chart" onMouseLeave={() => setHoveredPoint(null)}><ResponsiveContainer width="100%" height="100%"><LineChart data={chartData} margin={{ top: 12, right: 18, left: 2, bottom: 4 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#dfe7f1" vertical={true} />
      <XAxis dataKey="quarter" tick={{ fontSize: 11, fill: "#8794a6" }} axisLine={false} tickLine={false} />
      <YAxis domain={[yMin, yMax]} ticks={yTicks} tick={{ fontSize: 10, fill: "#8794a6" }} axisLine={false} tickLine={false} width={38} tickFormatter={(value: number) => value.toFixed(1)} />
      <Tooltip cursor={false} content={() => null} />
      {chartSeries.map(({ supplier, key, color }) => <Line key={key} type="monotone" dataKey={key} name={supplier} stroke={color} strokeWidth={2} strokeOpacity={0.88} dot={(dotProps: any) => {
        const quarter = String(dotProps.payload?.quarter || "");
        const score = Number(dotProps.payload?.[key] ?? 0);
        const showPoint = () => setHoveredPoint({ supplier, quarter, score, summary: String(dotProps.payload?.[`summary-${key}`] || ""), color, x: dotProps.cx, y: dotProps.cy });
        return <g onMouseEnter={showPoint} onMouseMove={showPoint} pointerEvents="all">
          <circle cx={dotProps.cx} cy={dotProps.cy} r={12} fill="transparent" stroke="transparent" />
          <circle cx={dotProps.cx} cy={dotProps.cy} r={4} fill={color} stroke={color} pointerEvents="none" />
        </g>;
      }} activeDot={{ r: 5 }} connectNulls />)}
    </LineChart></ResponsiveContainer>{hoveredPoint && <div className="tprm-trend-tooltip tprm-trend-tooltip-overlay" style={getTooltipPlacement(hoveredPoint)}><strong>{hoveredPoint.quarter}</strong><div><b style={{ color: hoveredPoint.color }}>{hoveredPoint.supplier}: {hoveredPoint.score.toFixed(1)}/10</b>{hoveredPoint.summary && <p>{hoveredPoint.summary}</p>}</div></div>}</div>
    <div className="tprm-trend-legend">{selectedSuppliers.map((supplier, index) => <span key={supplier}><i style={{ background: trendColor[index % trendColor.length] }} />{supplier}</span>)}</div>
  </section>;
}

const isAgentRow = (value: any): value is AgentRow =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  (
    Object.keys(value).some(key => normalizeKey(key) === "companyname") ||
    !!value.company_name ||
    !!value.companyName ||
    !!value.Supplier_Name
  );

const collectAgentRows = (value: any, depth = 0): AgentRow[] => {
  if (!value || depth > 8) return [];

  if (typeof value === "string") {
    const text = value.replace(/```json/gi, "").replace(/```/g, "").trim();
    if (!text.startsWith("{") && !text.startsWith("[")) return [];
    try {
      return collectAgentRows(JSON.parse(text), depth + 1);
    } catch {
      return [];
    }
  }

  if (Array.isArray(value)) {
    const rows = value.filter(isAgentRow);
    if (rows.length) return rows;
    return value.flatMap(item => collectAgentRows(item, depth + 1));
  }

  if (typeof value === "object") {
    if (isAgentRow(value)) return [value];
    if (value.answer && isAgentRow(value.answer)) return [value.answer];
    return Object.values(value).flatMap(item => collectAgentRows(item, depth + 1));
  }

  return [];
};

const mergeRiskResponses = (responses: any[]) => {
  const outputs = responses.map(response => response?.data?.output || response?.output).filter(Boolean);
  const firstOutput = outputs[0] || {};

  return {
    data: {
      output: {
        ...firstOutput,
        quarter_mapping: firstOutput.quarter_mapping || [],
        processing_warnings: outputs.flatMap(output => Array.isArray(output.processing_warnings) ? output.processing_warnings : []),
      },
    },
  };
};

// A single parameter gives its score directly ("9"); multiple parameters share a
// combined string ("Label: 7.4; Label2: 6.75") that needs matching back to each label.
const buildScoreEntries = (labels: string[], raw: unknown): ScoreEntry[] => {
  if (labels.length > 1) {
    const parsed = parseScoreEntries(raw);
    if (parsed.length) return parsed;
    const score = toNumber(raw);
    return labels.map(label => ({ label, score, note: "" }));
  }
  if (labels.length === 1) return [{ label: labels[0], score: toNumber(raw), note: "" }];
  return parseScoreEntries(raw);
};

const toRiskSupplier = (row: AgentRow, selected: AssessmentSupplier[], assessmentQuarter?: string): RiskSupplier => {
  const name = String(getField(row, "company_name", "companyName", "Supplier_Name", "Supplier") || "Unknown supplier").trim();
  const match = selected.find(item => normalizeKey(item.supplier || "") === normalizeKey(name));

  const rootLinks = splitList(getField(row, "Links"));
  const rootSources = Array.isArray(row?.sources) ? row.sources.map(String) : [];

  let parameters: string[] = [];
  let parameterScores: ScoreEntry[] = [];
  let subParameters: ScoreEntry[] = [];
  let parameterDetails: ParameterDetail[] = [];
  let collectedLinks: string[] = [...rootLinks, ...rootSources];

  if (Array.isArray(row.parameters) && row.parameters.length > 0 && typeof row.parameters[0] === "object") {
    for (const p of row.parameters) {
      const pName = String(p.parameter_name || p.name || "").trim();
      if (!pName) continue;

      const pScore = toNumber(p.parameter_score ?? p.score);
      const pConfidence = String(getField(p, "confidence_score", "confidenceScore", "Confidence Score") || "").trim();
      const pRiskClass = String(p.risk_classification || "").trim();
      const pSummary = String(p.parameter_summary || p.summary || "").trim();

      parameters.push(pName);
      parameterScores.push({
        label: pName,
        score: pScore,
        note: pRiskClass || pSummary,
      });

      const pSubDetails: SubParameterDetail[] = [];

      if (Array.isArray(p.sub_parameters)) {
        for (const sub of p.sub_parameters) {
          const subName = String(sub.sub_parameter_name || sub.name || "").trim();
          if (!subName) continue;

          const subScore = toNumber(sub.sub_parameter_score ?? sub.score);
          const subConfidence = String(getField(sub, "confidence_score", "confidenceScore", "Confidence Score") || "").trim();
          const subRationale = String(sub.score_rationale || sub.rationale || "").trim();
          const subBasis = String(sub.assessment_basis || "").trim();

          subParameters.push({
            label: subName,
            score: subScore,
            note: subRationale || subBasis,
          });

          const subSources: string[] = [];
          if (Array.isArray(sub.sources)) {
            sub.sources.forEach((src: any) => {
              if (typeof src === "string" && src.trim()) {
                subSources.push(src.trim());
                collectedLinks.push(src.trim());
              }
            });
          }

          const articles: Article[] = [];
          if (Array.isArray(sub.relevant_news_and_articles)) {
            sub.relevant_news_and_articles.forEach((art: any) => {
              if (art && typeof art === "object") {
                articles.push({
                  title: String(art.title || "").trim(),
                  publisher: String(art.publisher || "").trim(),
                  url: String(art.url || "").trim(),
                  summary: String(art.summary || "").trim(),
                });
                if (art.url && typeof art.url === "string") {
                  collectedLinks.push(art.url.trim());
                }
              }
            });
          }

          pSubDetails.push({
            name: subName,
            score: subScore,
            confidenceScore: subConfidence,
            rationale: subRationale,
            assessmentBasis: subBasis,
            articles,
            sources: subSources,
          });
        }
      }

      parameterDetails.push({
        name: pName,
        score: pScore,
        confidenceScore: pConfidence,
        riskClassification: pRiskClass,
        summary: pSummary,
        subParameters: pSubDetails,
      });
    }
  } else {
    parameters = splitList(getField(row, "Parameters"));
    const subParameterLabels = splitList(getField(row, "Sub Parameters", "Sub Parameter"));
    parameterScores = buildScoreEntries(parameters, getField(row, "Parameter Score"));
    subParameters = buildScoreEntries(subParameterLabels, getField(row, "Sub Parameter Score", "Sub Parameters Score"));
  }

  const overallRaw = getField(row, "overall_score", "Overall Score", "overallScore");
  const overall = overallRaw !== ""
    ? toNumber(overallRaw)
    : (parameterScores.length ? parameterScores.reduce((sum, entry) => sum + entry.score, 0) / parameterScores.length : 0);

  const overallRiskClass = String(row.overall_risk_classification || getField(row, "overall_risk_classification", "risk_classification") || "").trim();
  const news = String(getField(row, "ai_summary", "AI Summary", "Relevant news and article") || "");

  return {
    name,
    assessmentQuarter,
    region: String(getField(row, "Region", "Country", "region", "country") || match?.region || "-"),
    category: String(getField(row, "Category", "Industry", "category", "industry") || match?.industry || "-"),
    overall,
    overallRiskClassification: overallRiskClass,
    parameters,
    parameterScores,
    subParameters,
    news,
    links: Array.from(new Set(collectedLinks.filter(Boolean))),
    parameterDetails: parameterDetails.length > 0 ? parameterDetails : undefined,
  };
};

function CompletedDashboard({ suppliers, columns, trendData, quarterlySuppliers, selectedQuarter, onQuarterChange, onViewSupplier, onBack }: { suppliers: RiskSupplier[]; columns: string[]; trendData: RiskTrendData; quarterlySuppliers: Record<string, RiskSupplier[]>; selectedQuarter: string; onQuarterChange: (quarter: string) => void; onViewSupplier: (supplier: RiskSupplier) => void; onBack: () => void }) {
  const quarterOptions = Object.keys(quarterlySuppliers).sort((left, right) => quarterSortKey(left) - quarterSortKey(right));
  const displayedSuppliers = quarterlySuppliers[selectedQuarter] || suppliers;
  const total = suppliers.length;
  const averageOverall = total ? suppliers.reduce((sum, supplier) => sum + supplier.overall, 0) / total : 0;
  const onWatch = displayedSuppliers.filter(supplier => classificationBand(supplier.overallRiskClassification) === "high").length;
  const distribution = RISK_BANDS.map(band => [band.label, suppliers.filter(supplier => scoreBand(supplier.overall, supplier.overallRiskClassification) === band.className).length] as [string, number]);
  const maxBar = Math.max(1, ...distribution.map(([, value]) => value));
  const kpis: [string, string, string, JSX.Element, string][] = [
    ["TOTAL SUPPLIERS", String(total), `${total} assessed by agent`, <Building2 size={16} />, "blue"],
    ["RISK SCORE", `${averageOverall.toFixed(1)}/10`, "", <ShieldAlert size={16} />, "amber"],
    ["Spend Value", "$319M", "14% of total spend", <CircleDollarSign size={16} />, "violet"],
    ["SUPPLIERS ON WATCH", String(onWatch), "High risk suppliers", <AlertTriangle size={16} />, "green"],
    ["RATING SCORE", `${averageOverall.toFixed(1)}/10`, "Average supplier score", <Star size={16} />, "violet"],
  ];

  return <div className="tprm-completed-dashboard">
    <button className="tprm-back-link" onClick={onBack}><ArrowLeft size={14} /> Back to Supplier List</button>
    <div className="tprm-kpi-grid">{kpis.map(([label, value, sub, icon, tone]) => <article className={`tprm-kpi ${tone}`} key={label}><div><span>{label}</span><i>{icon}</i></div><strong>{value}</strong>{sub && <small>{sub}</small>}</article>)}</div>
    <SupplierRiskTrendChart trendData={trendData} />
    <Scorecard suppliers={displayedSuppliers} columns={columns} quarterOptions={quarterOptions} selectedQuarter={selectedQuarter} onQuarterChange={onQuarterChange} onViewSupplier={onViewSupplier} />
    <div className="tprm-chart-grid">
      <section className="tprm-panel tprm-chart">
        <div className="tprm-chart-head"><div><h3>Supplier risk matrix</h3><p>Probability of disruption vs business impact - by supplier</p></div><b>Risk matrix</b></div>
        <div className="tprm-matrix">
          <b className="monitor">Monitor</b>
          <b className="critical">Critical</b>
          <b className="observe">Manage</b>
          <b className="transfer">Transfer / Escalate</b>
          {suppliers.map(supplier => {
            const weakest = supplier.subParameters.length ? Math.min(...supplier.subParameters.map(entry => entry.score)) : supplier.overall;
            const band = scoreBand(supplier.overall, supplier.overallRiskClassification);
            return <button className={`tprm-matrix-point ${band}`} style={{ left: `${Math.min(92, Math.max(8, (10 - supplier.overall) * 10))}%`, top: `${Math.min(92, Math.max(8, (10 - weakest) * 10))}%` }} key={supplier.name} title={`${supplier.name} - ${supplier.overall}/10`} onClick={() => onViewSupplier(supplier)}></button>;
          })}
        </div>
        <div className="tprm-chart-legend">{suppliers.map(supplier => <span className={scoreBand(supplier.overall, supplier.overallRiskClassification)} key={supplier.name}>{supplier.name}</span>)}</div>
      </section>
      <section className="tprm-panel tprm-chart">
        <div className="tprm-chart-head"><div><h3>Risk distribution by tier</h3><p>Suppliers by risk rating — low to high risk</p></div><b>Distribution</b></div>
        <div className="tprm-bars">{distribution.map(([label, value]) => <div key={label}><strong>{value}</strong><i className={`tprm-risk-bar-${RISK_BANDS.find(band => band.label === label)?.className}`} style={{ height: `${Math.max(2, (value / maxBar) * 180)}px` }} /><span>{label}</span></div>)}</div>
      </section>
    </div>
  </div>;
}

function Scorecard({ suppliers, columns, quarterOptions = [], selectedQuarter = "", onQuarterChange, onViewSupplier, title = "Key Suppliers", initialView = "key" }: { suppliers: RiskSupplier[]; columns: string[]; quarterOptions?: string[]; selectedQuarter?: string; onQuarterChange?: (quarter: string) => void; onViewSupplier: (supplier: RiskSupplier) => void; title?: string; initialView?: "key" | "watchlist" }) {
  const [view, setView] = useState<"key" | "watchlist">(initialView);
  const [region, setRegion] = useState("all");
  const [rating, setRating] = useState("all");
  const [category, setCategory] = useState("all");
  const [supplierName, setSupplierName] = useState("all");
  const changeView = (nextView: "key" | "watchlist") => {
    setView(nextView);
    setRegion("all");
    setRating("all");
    setCategory("all");
    setSupplierName("all");
  };
  useEffect(() => { changeView(initialView); }, [initialView]);
  const uniqueValues = (values: string[]) => Array.from(new Set(values.filter(Boolean))).sort((left, right) => left.localeCompare(right));
  const regions = uniqueValues(suppliers.map(supplier => supplier.region));
  const categories = uniqueValues(suppliers.map(supplier => supplier.category));
  const supplierNames = uniqueValues(suppliers.map(supplier => supplier.name));
  const gridMinWidth = 32 + 170 + 100 + 130 + columns.length * 130 + 100;
  const watchlistSuppliers = suppliers.filter(supplier => classificationBand(supplier.overallRiskClassification) === "high");
  const visibleSuppliers = view === "watchlist" ? watchlistSuppliers : suppliers;
  const filteredSuppliers = visibleSuppliers.filter(supplier =>
    (region === "all" || supplier.region === region) &&
    (rating === "all" || scoreBand(supplier.overall, supplier.overallRiskClassification) === rating) &&
    (category === "all" || supplier.category === category) &&
    (supplierName === "all" || supplier.name === supplierName)
  );
  return <section className="tprm-panel tprm-completed-scorecard">
    <div className="tprm-chart-head"><div><h3>Supplier scorecard - composite rating</h3><p>Rating dimensions <b className="tprm-info">i</b></p></div><b>Scorecard</b></div>
    <div className="tprm-score-filters">
      {quarterOptions.length > 0 && <label><select value={selectedQuarter} onChange={event => onQuarterChange?.(event.target.value)} aria-label="Filter by quarter">{quarterOptions.map(value => <option key={value} value={value}>{value}</option>)}</select></label>}
      <label><select value={region} onChange={event => setRegion(event.target.value)} aria-label="Filter by region"><option value="all">All regions</option>{regions.map(value => <option key={value} value={value}>{value}</option>)}</select></label>
      <label><select value={rating} onChange={event => setRating(event.target.value)} aria-label="Filter by rating"><option value="all">All ratings</option><option value="low">Low Risk</option><option value="medium">Medium Risk</option><option value="high">High Risk</option></select></label>
      <label><select value={category} onChange={event => setCategory(event.target.value)} aria-label="Filter by category"><option value="all">All categories</option>{categories.map(value => <option key={value} value={value}>{value}</option>)}</select></label>
      <label><select value={supplierName} onChange={event => setSupplierName(event.target.value)} aria-label="Filter by supplier"><option value="all">All suppliers</option>{supplierNames.map(value => <option key={value} value={value}>{value}</option>)}</select></label>
      <span>{filteredSuppliers.length} of {visibleSuppliers.length} suppliers</span>
    </div>
    <div className="tprm-scorecard-tabs" role="tablist" aria-label="Supplier scorecard views">
      <button className={view === "key" ? "active" : ""} onClick={() => changeView("key")} role="tab" aria-selected={view === "key"}>Key Suppliers</button>
      <button className={view === "watchlist" ? "active" : ""} onClick={() => changeView("watchlist")} role="tab" aria-selected={view === "watchlist"}>Watchlist &amp; Action</button>
    </div>
    <div className="tprm-score-table">
      <div className="tprm-scorecard-title"><h4>{view === "watchlist" ? "Watchlist & Mitigation" : title}</h4><b className={view === "watchlist" ? "tprm-high-risk-count" : ""}>{view === "watchlist" ? `${filteredSuppliers.length} high risk` : `${filteredSuppliers.length} suppliers`}</b></div>
      <div className="tprm-dyn-scroll">
        <table className="tprm-scorecard-table" style={{ minWidth: gridMinWidth }}>
          <colgroup>
            <col style={{ width: 32 }} />
            <col style={{ width: 170 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 130 }} />
            {columns.map(column => <col style={{ width: 130 }} key={column} />)}
            <col style={{ width: 100 }} />
          </colgroup>
          <thead>
            <tr className="tprm-score-head">
              <th className="tprm-sticky-index" scope="col">#</th>
              <th className="tprm-sticky-supplier" scope="col">Supplier</th>
              <th scope="col">Region</th>
              <th scope="col">Category</th>
              {columns.map(column => <th scope="col" key={column} title={column}>{column}</th>)}
              <th className="tprm-sticky-action" scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredSuppliers.map((supplier, index) => <tr className={`tprm-score-row ${classificationBand(supplier.overallRiskClassification) === "high" ? "is-high-risk" : ""}`} key={`${normalizeKey(supplier.name)}-${index}`}>
              <td className="tprm-sticky-index">{index + 1}</td>
              <th className="tprm-sticky-supplier" scope="row">{supplier.name}</th>
              <td>{supplier.region}</td>
              <td>{supplier.category}</td>
              {columns.map(column => {
                const entry = findParameterScore(column, supplier.parameterScores);
                return <td className={entry ? `tprm-text-${scoreBand(entry.score, entry.note)}` : ""} key={column} title={entry?.label || ""}>{entry ? `${entry.score}/10` : "-"}</td>;
              })}
              <td className="tprm-score-actions tprm-sticky-action">
                <button className="tprm-icon-action" onClick={() => onViewSupplier(supplier)} aria-label={`View ${supplier.name} risk details`} title="View risk details">
                  <span>
                    View Details
                  </span>
                </button>
                {view === "watchlist" && <button className="tprm-icon-action is-alert" onClick={() => toast.success("Alert sent successfully to the supplier")} aria-label={`Alert ${supplier.name}`} title="Send supplier alert"><Bell size={14} /></button>}
              </td>
            </tr>)}
          </tbody>
        </table>
        {!filteredSuppliers.length && <p className="tprm-table-message">No suppliers match the selected filters.</p>}
      </div>
    </div>
  </section>;
}

function AddSupplierDrawer({ onClose, onAdd }: { onClose: () => void; onAdd: (supplier: AssessmentSupplier) => Promise<void> }) {
  const [supplier, setSupplier] = useState("");
  const [region, setRegion] = useState("");
  const [industry, setIndustry] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!supplier.trim()) return;

    setIsSaving(true);
    setError("");

    try {
      await onAdd({ supplier: supplier.trim(), region: region.trim() || null, industry: industry.trim() || null });
    } catch (err: any) {
      setError(err?.message || "Unable to add supplier.");
      setIsSaving(false);
    }
  };

  return <>
    <button className="tprm-drawer-backdrop" onClick={onClose} aria-label="Close add supplier" />
    <aside className="tprm-drawer tprm-form-drawer">
      <div className="tprm-drawer-header"><h3>Add Supplier</h3><button className="tprm-icon-button" onClick={onClose}><X size={18} /></button></div>
      <form className="tprm-form" onSubmit={submit}>
        <label><span>Supplier</span><input value={supplier} onChange={event => setSupplier(event.target.value)} placeholder="Linde India" autoFocus /></label>
        <label><span>Region</span><input value={region} onChange={event => setRegion(event.target.value)} placeholder="Asia Pacific" /></label>
        <label><span>Category</span><input value={industry} onChange={event => setIndustry(event.target.value)} placeholder="Industrial Gases" /></label>
        {error && <p className="tprm-form-error">{error}</p>}
        <div className="tprm-form-actions"><button className="tprm-ghost-button" type="button" onClick={onClose}>Cancel</button><button className="tprm-gradient-button" type="submit" disabled={!supplier.trim() || isSaving}>{isSaving ? "Adding..." : "Add"}</button></div>
      </form>
    </aside>
  </>;
}

function BulkUploadDrawer({ onClose }: { onClose: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");

  return <>
    <button className="tprm-drawer-backdrop" onClick={onClose} aria-label="Close bulk upload" />
    <aside className="tprm-drawer tprm-form-drawer">
      <div className="tprm-drawer-header"><h3>Upload Supplier</h3><button className="tprm-icon-button" onClick={onClose}><X size={18} /></button></div>
      <div className="tprm-dropzone">
        <i><Upload size={18} /></i>
        <p>Drag &amp; Drop your files here or <button type="button" onClick={() => inputRef.current?.click()}>Browse</button></p>
        <small>{fileName || "File Supported (.xls, .csv Max-size: 100mb)"}</small>
        <input ref={inputRef} type="file" accept=".xls,.xlsx,.csv" hidden onChange={event => setFileName(event.target.files?.[0]?.name || "")} />
      </div>
      <div className="tprm-form-actions"><button className="tprm-ghost-button" type="button" onClick={onClose}>Cancel</button><button className="tprm-gradient-button" type="button" disabled>Upload</button></div>
    </aside>
  </>;
}

function SupplierDetail({ supplier, onClose }: { supplier: RiskSupplier; onClose: () => void }) {
  const subParameters = supplier.subParameters;
  const band = scoreBand(supplier.overall, supplier.overallRiskClassification);

  return <>
    <button className="tprm-drawer-backdrop" onClick={onClose} aria-label="Close supplier detail" />
    <aside className="tprm-drawer tprm-risk-drawer" style={{ width: "min(560px, 100vw)" }}>
      <div className="tprm-drawer-header tprm-profile-header">
        <div>
          <h3>{supplier.name} — Risk Profile</h3>
          <p>Supplier · {supplier.category} · {supplier.region}</p>
        </div>
        <div>
          {classificationBand(supplier.overallRiskClassification) === "high" && <button className="tprm-icon-button tprm-notification-button" onClick={() => toast.success("Alert sent successfully to the supplier")} aria-label={`Notify ${supplier.name}`} title="Notify supplier"><Bell size={16} /></button>}
          <button className="tprm-icon-button" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </div>
      </div>

      

      {supplier.parameterDetails && supplier.parameterDetails.length > 0 ? (
        <section className="tprm-drawer-section">
    
          {supplier.parameterDetails.map((param, index) => (
            <details className="tprm-parameter-card" key={param.name} open={index === 0}>
              <summary><span><strong>{param.name}</strong></span><b className="tprm-health-score">{param.score}/10</b></summary>
              <div className="tprm-parameter-body">
                <div className="tprm-parameter-meta"><span><small>OVERALL SCORE</small><ScoreWithBar score={param.score} /></span><span><small>CONFIDENCE</small><b>{param.confidenceScore || "HIGH"}</b></span><span><small>STRENGTH</small><b>{healthLabel(param.score)}</b></span></div>
                {param.summary && <p className="tprm-parameter-summary">{param.summary}</p>}
                {param.subParameters && param.subParameters.length > 0 && <div className="tprm-subparameter-list">
                  {param.subParameters.map(sub => <article key={sub.name}>
                    <div><strong>{sub.name}</strong><b className="tprm-health-score">{sub.score}/10</b></div>
                    <div className="tprm-subparameter-meta"><span><small>RISK SCORE</small><ScoreWithBar score={sub.score} /></span><span><small>CONFIDENCE</small><b>{sub.confidenceScore || "HIGH"}</b></span><span><small>STRENGTH</small><b>{healthLabel(sub.score)}</b></span></div>
                    {sub.assessmentBasis && <p>{sub.assessmentBasis}</p>}{sub.rationale && <p>{sub.rationale}</p>}
                    {(() => {
                      const publishers = Array.from(new Set((sub.articles || []).map(article => article.publisher).filter(Boolean)));
                      const references = (sub.articles || []).filter(article => article.url);
                      const sourceNames = publishers.length ? publishers : (sub.sources || []);
                      return (sourceNames.length || references.length) ? <div className="tprm-evidence">
                        {sourceNames.length > 0 && <div className="tprm-evidence-sources"><small>SOURCES</small><p>{sourceNames.join(", ")}</p></div>}
                        {references.length > 0 && <div className="tprm-evidence-links"><small>REFERENCES</small><div>{references.slice(0, 5).map((article, articleIndex) => <a href={article.url} target="_blank" rel="noopener noreferrer" key={`${article.url}-${articleIndex}`}>{article.title || article.publisher || "Reference"}</a>)}</div></div>}
                      </div> : null;
                    })()}
                  </article>)}
                </div>}
              </div>
            </details>
          ))}
        </section>
      ) : (
        <section className="tprm-risk-meters" aria-label="Sub-parameter scores">
          {subParameters.map(entry => (
            <div className="tprm-meter" key={entry.label}>
              <div>
                <span>{entry.label}{entry.note ? ` · ${entry.note}` : ""}</span>
                <strong>{entry.score}/10</strong>
              </div>
              <i><b className={`risk-bar-${scoreBand(entry.score)}`} style={{ width: `${Math.min(100, entry.score * 10)}%` }} /></i>
            </div>
          ))}
          {!subParameters.length && <p className="tprm-no-subparameters">No sub-parameter scores returned by the assessment.</p>}
        </section>
      )}

      {supplier.news && (
        <section className="tprm-mitigation tprm-ai-mitigation">
          <h4>AI Assessment &amp; Summary</h4>
          <div style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5, color: "#333" }}>
            {supplier.news}
          </div>
        </section>
      )}

     
    </aside>
  </>;
}

export function SupplierRiskAnalysisView() {
  const { runSpendAIRisk } = useSimplAI();
  const [tab, setTab] = useState<Tab>("overview");
  const [phase, setPhase] = useState<Phase>("select");
  const [assessmentSuppliers, setAssessmentSuppliers] = useState<AssessmentSupplier[]>([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(true);
  const [supplierError, setSupplierError] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [progress, setProgress] = useState(0);
  const [detail, setDetail] = useState<RiskSupplier | null>(null);
  const [completedActions, setCompletedActions] = useState<Set<string>>(new Set());
  const [riskSuppliers, setRiskSuppliers] = useState<RiskSupplier[]>([]);
  const [riskTrendData, setRiskTrendData] = useState<RiskTrendData>(emptyRiskTrendData);
  const [quarterlyRiskSuppliers, setQuarterlyRiskSuppliers] = useState<Record<string, RiskSupplier[]>>({});
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [runError, setRunError] = useState("");
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [addMode, setAddMode] = useState<AddMode>(null);

  useEffect(() => {
    if (phase !== "running") return;
    const timer = window.setInterval(() => setProgress(value => (value >= 92 ? value : value + 4)), 1500);
    return () => window.clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (phase === "complete") toast.success("Risk assessment completed successfully", {
      icon: <Check size={15} />,
      style: { background: "#eefbf4", border: "1px solid #91d5b4", color: "#16623e", fontWeight: 700 },
    });
  }, [phase]);

  useEffect(() => {
    let isMounted = true;

    const loadSuppliers = async () => {
      try {
        const response = await apiRequest("/api/thirdparty-suppliers");
        if (isMounted) setAssessmentSuppliers(response.data ?? []);
      } catch (error) {
        if (isMounted) setSupplierError("Unable to load suppliers from the database.");
      } finally {
        if (isMounted) setIsLoadingSuppliers(false);
      }
    };

    loadSuppliers();
    return () => { isMounted = false; };
  }, []);

  const parameterColumns = useMemo(() => {
    const columns: string[] = [];
    riskSuppliers.forEach(supplier => supplier.parameters.forEach(parameter => {
      if (!columns.some(column => canonicalParameterKey(column) === canonicalParameterKey(parameter))) columns.push(parameter);
    }));
    return columns;
  }, [riskSuppliers]);

  const runAssessment = async () => {
    const selectedSuppliers = assessmentSuppliers.filter((_, index) => selected.has(index));
    if (!selectedSuppliers.length) return;

    setRunError("");
    setProgress(4);
    setPhase("running");

    try {
      const runtimeSuppliers = selectedSuppliers.filter(item => item.assessmentSource === "runtime");
      const existingSuppliers = selectedSuppliers.filter(item => item.assessmentSource !== "runtime");
      let databaseResults: any[] = [];

      if (existingSuppliers.length) {
        const databaseResponse = await apiRequest("/api/supplier-risk-assessments/batch", {
          method: "POST",
          body: JSON.stringify({
            supplierIds: existingSuppliers.map(item => Number(item.id)).filter(id => Number.isInteger(id) && id > 0),
            supplierNames: existingSuppliers.map(item => item.supplier),
            toolId: SPEND_AI_RISK_TOOL_ID,
          }),
        });
        const entries = (databaseResponse?.data?.assessments || []).filter((entry: any) => collectAgentRows(entry.responseData).length > 0);
        const returnedNames = new Set<string>(entries.flatMap((entry: any) => collectAgentRows(entry.responseData).map(row =>
          normalizeKey(String(getField(row, "company_name", "companyName", "Supplier_Name", "Supplier")))
        )));
        const missingSuppliers = existingSuppliers.filter(item => !returnedNames.has(normalizeKey(item.supplier)));
        if (missingSuppliers.length) {
          throw new Error(`No database risk assessment found for: ${missingSuppliers.map(item => item.supplier).join(", ")}. Existing suppliers require stored assessments; the runtime agent is only used for newly added suppliers.`);
        }
        databaseResults = entries.map((entry: any) => entry.responseData);
      }

      let agentResult: any = null;
      if (runtimeSuppliers.length) {
        const payload = { data: { input_data: runtimeSuppliers.map(item => ({ Supplier_Name: item.supplier, URL: "" })) } };
        agentResult = await runSpendAIRisk(payload, SPEND_AI_RISK_TOOL_ID);

        try {
          const saved = await apiRequest("/api/supplier-risk-assessments", {
            method: "POST",
            body: JSON.stringify({ toolId: SPEND_AI_RISK_TOOL_ID, responseData: agentResult }),
          });
          const storedNames = new Set<string>((saved?.data?.stored || []).map((item: any) => normalizeKey(item.supplierName)));
          setAssessmentSuppliers(current => current.map(item => storedNames.has(normalizeKey(item.supplier)) ? { ...item, assessmentSource: "database" } : item));
        } catch (databaseError) {
          console.warn("Supplier assessment completed but could not be saved to the database.", databaseError);
        }
      }

      const result = mergeRiskResponses([...databaseResults, ...(agentResult ? [agentResult] : [])]);
      const rows = collectAgentRows(result?.data ?? result);

      if (!rows.length) throw new Error("No supplier risk assessment results were returned.");

      const uniqueRows = Array.from(new Map(rows.map(row => {
        const companyName = String(getField(row, "company_name", "companyName", "Supplier_Name", "Supplier")).trim();
        return [normalizeKey(companyName), row] as const;
      })).values());
      setRiskSuppliers(uniqueRows.map(row => toRiskSupplier(row, selectedSuppliers)));
      setRiskTrendData(buildRiskTrendData(result));
      const quarterlySuppliers = buildQuarterlyRiskSuppliers(result, selectedSuppliers);
      setQuarterlyRiskSuppliers(quarterlySuppliers);
      const quarters = Object.keys(quarterlySuppliers).sort((left, right) => quarterSortKey(left) - quarterSortKey(right));
      setSelectedQuarter(quarters[quarters.length - 1] || "");
      setProgress(100);
      setPhase("complete");
    } catch (error: any) {
      setRunError(error?.message || "Risk assessment failed.");
      setProgress(0);
      setPhase("select");
    }
  };

  const addSupplier = async (supplier: AssessmentSupplier) => {
    const response = await apiRequest("/api/thirdparty-suppliers", { method: "POST", body: JSON.stringify({ supplier: supplier.supplier, region: supplier.region ?? "", industry: supplier.industry ?? "" }) });
    const created: AssessmentSupplier = { ...(response?.data ?? supplier), assessmentSource: "runtime" };

    setAssessmentSuppliers(current => [created, ...current]);
    setSelected(value => new Set([0, ...Array.from(value).map(index => index + 1)]));
    setAddMode(null);
  };

  const toggleSupplier = (index: number) => setSelected(value => {
    const next = new Set(value);
    next.has(index) ? next.delete(index) : next.add(index);
    return next;
  });
  const toggleAll = () => setSelected(value => value.size === assessmentSuppliers.length ? new Set() : new Set(assessmentSuppliers.map((_, index) => index)));
  const completedSteps = Math.ceil(progress / (100 / steps.length));
  const tabs: [Tab, string][] = [["overview", "Risk Overview"], ["watchlist", "Watchlist & Actions"]];

  return <div className="tprm-view">
    <div className="tprm-header"><p className="tprm-eyebrow">Supplier</p><h2>Third Party/Supplier Risk Management</h2></div>
    {/* <div className="tprm-tabs">{tabs.map(([key, label]) => <button className={tab === key ? "active" : ""} onClick={() => setTab(key)} key={key}>{label}</button>)}</div> */}
    {tab === "overview" && <div className="tprm-content">
      {phase === "select" && <section className="tprm-panel tprm-assessment"><div className="tprm-panel-header"><div><h3>Supplier risk assessment</h3><p>Select suppliers to run risk assessment <b>•</b> sources: ERP vendor master, D&amp;B, sanctions feeds, ESG disclosures</p></div><div className="tprm-run-control"><span>{selected.size} of {assessmentSuppliers.length} suppliers selected</span><button className="tprm-primary-button" disabled={!selected.size || isLoadingSuppliers} onClick={runAssessment}>Run Risk Assessment</button><div className="tprm-add-supplier"><button className="tprm-outline-button" onClick={() => setIsAddMenuOpen(open => !open)}>Add Supplier <ChevronDown size={14} /></button>{isAddMenuOpen && <><button className="tprm-menu-backdrop" aria-label="Close menu" onClick={() => setIsAddMenuOpen(false)} /><div className="tprm-add-menu"><button onClick={() => { setIsAddMenuOpen(false); setAddMode("single"); }}>Add Single Supplier</button><button onClick={() => { setIsAddMenuOpen(false); setAddMode("bulk"); }}>Bulk Upload Supplier</button></div></>}</div></div></div>{runError && <div className="tprm-table-message is-error">{runError}</div>}<div className="tprm-supplier-table"><div className="tprm-select-row tprm-table-head"><label><input type="checkbox" checked={assessmentSuppliers.length > 0 && selected.size === assessmentSuppliers.length} onChange={toggleAll} disabled={isLoadingSuppliers || assessmentSuppliers.length === 0} /> <span>Supplier</span></label><span>Region</span><span>Category</span><span>Status</span></div>{isLoadingSuppliers && <div className="tprm-table-message">Loading suppliers...</div>}{supplierError && <div className="tprm-table-message is-error">{supplierError}</div>}{!isLoadingSuppliers && !supplierError && assessmentSuppliers.length === 0 && <div className="tprm-table-message">No suppliers found.</div>}{assessmentSuppliers.map(({ supplier, region, industry }, index) => <label className={`tprm-select-row ${selected.has(index) ? "selected" : ""}`} key={`${supplier}-${index}`}><input type="checkbox" checked={selected.has(index)} onChange={() => toggleSupplier(index)} /><strong>{supplier}</strong><span>{region || "-"}</span><span>{industry || "-"}</span><b>{selected.has(index) ? "Selected" : "Unevaluated"}</b></label>)}{selected.size > 0 && <div className="tprm-selection-footer">{selected.size} of {assessmentSuppliers.length} suppliers selected</div>}</div></section>}
      {phase === "running" && <section className="tprm-panel tprm-progress-panel">
        <div className="tprm-progress-ring" style={{ background: `conic-gradient(#6a3d80 ${progress}%, #eee6f1 0)` }}><div className="tprm-progress-ring__inner"><Cpu size={20} /></div></div>
        <h3>Running Risk Assessment...</h3>
        <p>Modeling composite risk scores across {selected.size} selected partners</p>
        <div className="tprm-progress"><b style={{ width: `${progress}%` }} /></div>
        <div className="tprm-run-steps-divider" />
        <div className="tprm-run-steps">{steps.map((step, index) => {
          const state = index < completedSteps ? "done" : index === completedSteps ? "current" : "pending";
          return <div className={state} key={step}><i>{state === "done" ? <Check size={12} /> : state === "current" ? <span className="tprm-run-steps__dot" /> : null}</i><span>{step}</span></div>;
        })}</div>
      </section>}
      {phase === "complete" && <CompletedDashboard suppliers={riskSuppliers} columns={parameterColumns} trendData={riskTrendData} quarterlySuppliers={quarterlyRiskSuppliers} selectedQuarter={selectedQuarter} onQuarterChange={setSelectedQuarter} onViewSupplier={setDetail} onBack={() => { setPhase("select"); setSelected(new Set()); }} />}
    </div>}
    {tab === "scorecard" && <div className="tprm-content"><Scorecard suppliers={riskSuppliers} columns={parameterColumns} onViewSupplier={setDetail} /></div>}
    {tab === "geo" && <div className="tprm-content"><section className="tprm-panel"><p className="tprm-eyebrow">Geopolitical & financial</p><h3>Exposure monitor</h3><p>External market signals weighted by supplier dependency and spend concentration.</p><div className="tprm-geo-grid"><div className="tprm-region-map"><Globe2 size={46} /><p>Regional supply exposure</p></div><div className="tprm-signal-list">{riskSuppliers.filter(supplier => supplier.overall < 8).map(supplier => <div key={supplier.name}><b className={`tprm-status ${scoreBand(supplier.overall)}`}>{scoreBand(supplier.overall)}</b><strong>{supplier.name}</strong><span>{supplier.region} · {supplier.category}</span></div>)}{!riskSuppliers.length && <div className="tprm-table-message">Run a risk assessment to see exposure signals.</div>}</div></div></section></div>}
    {tab === "watchlist" && <div className="tprm-content"><Scorecard suppliers={quarterlyRiskSuppliers[selectedQuarter] || riskSuppliers} columns={parameterColumns} quarterOptions={Object.keys(quarterlyRiskSuppliers).sort((left, right) => quarterSortKey(left) - quarterSortKey(right))} selectedQuarter={selectedQuarter} onQuarterChange={setSelectedQuarter} onViewSupplier={setDetail} title="Watchlist & Mitigation" initialView="watchlist" /></div>}
    {detail && <SupplierDetail supplier={detail} onClose={() => setDetail(null)} />}
    {addMode === "single" && <AddSupplierDrawer onClose={() => setAddMode(null)} onAdd={addSupplier} />}
    {addMode === "bulk" && <BulkUploadDrawer onClose={() => setAddMode(null)} />}
  </div>;
}
